/**
 * Matchmaking hook — manages queue entry, searching state, and match results.
 * Uses Ditto as the default relay for matchmaking events.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useNostrUser, useNostrPublisher, useNostrSubscription } from "./useNostr";
import type { QueueEntry, MatchPair } from "@/lib/matchmaking";
import { buildQueueEvent, buildReadyEvent } from "@/lib/matchmaking";
import {
  KIND_MATCHMAKING_QUEUE,
  KIND_MATCH_FOUND,
  KIND_READY,
  TAG_GAME_TYPE,
  TAG_GAME_ID,
  TAG_TEAM_A,
  TAG_TEAM_B,
} from "@/lib/gameKinds";
import type { NostrEvent } from "@nostrify/types";

export type MatchmakingPhase =
  | "idle"
  | "readying"
  | "searching"
  | "matched"
  | "playing";

export interface MatchmakingState {
  phase: MatchmakingPhase;
  gameType: string | null;
  queueEntry: QueueEntry | null;
  match: MatchPair | null;
  readyMembers: Set<string>;
  searchStartTime: number | null;
  error: string | null;
}

export function useMatchmaking(teamId: string | null, teamName: string, memberPubkeys: string[]) {
  const { user } = useNostrUser();
  const { publish } = useNostrPublisher();
  const { subscribe } = useNostrSubscription();

  const [state, setState] = useState<MatchmakingState>({
    phase: "idle",
    gameType: null,
    queueEntry: null,
    match: null,
    readyMembers: new Set(),
    searchStartTime: null,
    error: null,
  });

  // Toggle ready status
  const toggleReady = useCallback(async (gameType: string) => {
    if (!user || !teamId) return;

    setState((prev) => {
      const newReady = new Set(prev.readyMembers);
      if (newReady.has(user.pubkey)) {
        newReady.delete(user.pubkey);
      } else {
        newReady.add(user.pubkey);
      }

      // Publish ready event to relays
      const event = buildReadyEvent(teamId, gameType, user.pubkey, newReady.has(user.pubkey));
      publish(event).catch(console.error);

      return {
        ...prev,
        gameType,
        readyMembers: newReady,
        phase: newReady.size >= 2 ? "readying" : "idle",
      };
    });
  }, [user, teamId, publish]);

  // Start matchmaking
  const startMatchmaking = useCallback(async (gameType: string, rating: number) => {
    if (!user || !teamId) return;

    const entry: QueueEntry = {
      teamId,
      teamName,
      gameType,
      rating,
      memberCount: memberPubkeys.length,
      memberPubkeys,
      enteredAt: Math.floor(Date.now() / 1000),
    };

    const event = buildQueueEvent(entry);
    await publish(event);

    setState((prev) => ({
      ...prev,
      phase: "searching",
      queueEntry: entry,
      searchStartTime: Date.now(),
      error: null,
    }));
  }, [user, teamId, teamName, memberPubkeys, publish]);

  // Cancel matchmaking
  const cancelMatchmaking = useCallback(() => {
    setState((prev) => ({
      ...prev,
      phase: "idle",
      queueEntry: null,
      searchStartTime: null,
      error: null,
    }));
  }, []);

  // Subscribe to match events
  useEffect(() => {
    if (!teamId || state.phase !== "searching") return;

    const cleanup = subscribe(
      [
        {
          kinds: [KIND_MATCH_FOUND],
          "#t": [teamId],
          since: Math.floor(Date.now() / 1000) - 10,
        },
      ],
      (event: NostrEvent) => {
        const tags = event.tags;
        const gameId = tags.find((t) => t[0] === TAG_GAME_ID)?.[1];
        const teamA = tags.find((t) => t[0] === TAG_TEAM_A)?.[1];
        const teamB = tags.find((t) => t[0] === TAG_TEAM_B)?.[1];
        const gameType = tags.find((t) => t[0] === TAG_GAME_TYPE)?.[1];
        const teamAName = tags.find((t) => t[0] === "team_a_name")?.[1] ?? "Team A";
        const teamBName = tags.find((t) => t[0] === "team_b_name")?.[1] ?? "Team B";

        if (!gameId || !teamA || !teamB || !gameType) return;

        const match: MatchPair = {
          gameId,
          teamA: {
            teamId: teamA,
            teamName: teamAName,
            gameType,
            rating: 1200,
            memberCount: 0,
            memberPubkeys: [],
            enteredAt: 0,
          },
          teamB: {
            teamId: teamB,
            teamName: teamBName,
            gameType,
            rating: 1200,
            memberCount: 0,
            memberPubkeys: [],
            enteredAt: 0,
          },
          matchedAt: Math.floor(Date.now() / 1000),
        };

        setState((prev) => ({ ...prev, phase: "matched", match }));
      },
    );

    return cleanup;
  }, [teamId, state.phase, subscribe]);

  // Listen for other members' ready states
  useEffect(() => {
    if (!teamId || !state.gameType) return;

    const cleanup = subscribe(
      [
        {
          kinds: [KIND_READY],
          "#team_id": [teamId],
          "#g": [state.gameType],
          since: Math.floor(Date.now() / 1000) - 30,
        },
      ],
      (event: NostrEvent) => {
        const pubkey = event.pubkey;
        const isReady = event.tags.find((t) => t[0] === "ready")?.[1] === "true";

        setState((prev) => {
          const newReady = new Set(prev.readyMembers);
          if (isReady) newReady.add(pubkey);
          else newReady.delete(pubkey);
          return { ...prev, readyMembers: newReady };
        });
      },
    );

    return cleanup;
  }, [teamId, state.gameType, subscribe]);

  const isReady = user ? state.readyMembers.has(user.pubkey) : false;

  return { ...state, isReady, toggleReady, startMatchmaking, cancelMatchmaking };
}